import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { AccountDTO, LocationDTO } from '@invenco/accounts-interface';
import {
  CarrierDTO,
  ShipmentClassification,
  ShippingPriceBandDTO,
  ShippingPriceCarrierPriceDTO,
  ShippingPriceDTO,
} from '@invenco/shipping-interface';

import { BreadCrumb } from '@/components-ant/header';
import { fetchAllPages } from '@/shared/helpers';

import { useGateways } from '../../../../gateways/GatewayProvider';
import { Result } from '../../../../shared/helpers/Result';
import {
  useEntityDetailsQuery,
  useGatewayMutation,
  useQueryWithInput,
} from '../../../../shared/hooks/queries';
import { ComponentData } from '../../../../shared/types';
import { sortBands } from '../sortBands';

type Models = {
  isNew: boolean;
  isSaving: boolean;
  isLoading: boolean;
  isLoadingAccounts: boolean;
  isLoadingCarriers: boolean;
  isDetailsModalOpen: boolean;
  isCarrierSelectionModalOpen: boolean;
  hasChanges: boolean;
  canSave: boolean;
  shippingPrice: Partial<ShippingPriceDTO>;
  accounts: AccountDTO[];
  account?: AccountDTO;
  carrierOptions: CarrierDTO[];
  location?: LocationDTO;
  breadcrumbs: BreadCrumb[];
  existingName?: string;
  availableClassifications: ShipmentClassification[];
  existingCarrierClassifications: Array<{
    carrierId: string;
    classification: ShipmentClassification | null;
  }>;
};

type Operations = {
  refresh: () => Promise<void>;
  updateDetails: (data: Partial<ShippingPriceDTO>) => void;
  save: () => Promise<Result>;
  addNewGroup: (carrier: CarrierDTO, classification: ShipmentClassification | null) => void;
  removeGroup: (groupIndex: number) => void;
  addBand: (band: ShippingPriceBandDTO) => void;
  addGroupBand: (groupIndex: number, band: ShippingPriceBandDTO) => void;
  updateBand: (index: number, band: Partial<ShippingPriceBandDTO>) => void;
  updateGroupBand: (
    groupIndex: number,
    bandIndex: number,
    band: Partial<ShippingPriceBandDTO>,
  ) => void;
  removeBand: (index: number) => void;
  removeGroupBand: (groupIndex: number, bandIndex: number) => void;
  addClassifiedGroup: (classification: ShipmentClassification) => void;
  removeClassifiedGroup: (classifiedIndex: number) => void;
  addClassifiedBand: (classifiedIndex: number, band: ShippingPriceBandDTO) => void;
  updateClassifiedBand: (
    classifiedIndex: number,
    bandIndex: number,
    band: Partial<ShippingPriceBandDTO>,
  ) => void;
  removeClassifiedBand: (classifiedIndex: number, bandIndex: number) => void;
  openDetailsModal: () => void;
  closeDetailsModal: () => void;
  openCarrierSelectionModal: () => void;
  closeCarrierSelectionModal: () => void;
};

export function useShippingPriceDetailsPage(): ComponentData<Models, Operations> {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';
  const { shippingGateway, accountsGateway } = useGateways();
  const navigate = useNavigate();

  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [shippingPrice, setShippingPrice] = useState<Partial<ShippingPriceDTO>>({
    validFrom: new Date().toISOString(),
    priceBands: [],
    classifiedPriceBands: [],
  });
  const [isCarrierSelectionModalOpen, setIsCarrierSelectionModalOpen] = useState(false);

  const {
    data: shippingPriceData,
    isLoading,
    refetch,
  } = useEntityDetailsQuery({
    parentKey: 'shippingPrices',
    id,
    isNew,
    query: (fetchId, { signal }) => shippingGateway.getShippingPrice(fetchId, { signal }),
  });

  const {
    data: allAccounts,
    isLoading: isLoadingAccounts,
    refetch: refetchAccounts,
  } = useQuery({
    queryKey: ['accounts'],
    queryFn: ({ signal }) =>
      fetchAllPages(({ cursor, take }) =>
        accountsGateway.getAccounts({ cursor, take }, { signal }),
      ),
  });

  const {
    data: allCarriers,
    isLoading: isLoadingCarriers,
    refetch: refetchCarriers,
  } = useQuery({
    queryKey: ['carriers'],
    queryFn: ({ signal }) => shippingGateway.getCarriers(undefined, { signal }),
  });

  const existingName = shippingPriceData?.name;
  const breadcrumbs = useMemo<BreadCrumb[]>(
    () => [
      { url: '/shipping', title: 'Shipping' },
      { url: '/shipping/prices', title: 'Prices' },
      {
        url: `/shipping/prices/${id}`,
        title: isNew ? 'New Price' : existingName,
        loading: isLoading,
      },
    ],
    [id, isNew, isLoading, existingName],
  );

  const { data: accountDetails, refetch: refetchAccountDetails } = useQueryWithInput({
    parentKey: 'account',
    input: shippingPrice.accountId,
    query: (input, { signal }) => accountsGateway.getAccount(input!, { signal }),
    enabled: !!shippingPrice.accountId,
  });

  const account = useMemo(
    () => allAccounts?.find((a) => a.id === shippingPrice.accountId),
    [allAccounts, shippingPrice.accountId],
  );
  const location = useMemo(
    () => accountDetails?.locations?.find((l) => l.id === shippingPrice.locationId),
    [accountDetails, shippingPrice.locationId],
  );

  const availableClassifications = useMemo(() => {
    const existing = new Set(
      shippingPrice.classifiedPriceBands?.map((cpb) => cpb.classification) ?? [],
    );
    return Object.values(ShipmentClassification).filter((c) => !existing.has(c));
  }, [shippingPrice.classifiedPriceBands]);

  const existingCarrierClassifications = useMemo(
    () =>
      (shippingPrice.carrierPrices ?? []).map(({ carrierId, classification }) => ({
        carrierId,
        classification: classification ?? null,
      })),
    [shippingPrice.carrierPrices],
  );

  const canSave = useMemo(
    () =>
      Boolean(
        shippingPrice.name &&
        shippingPrice.accountId &&
        shippingPrice.validFrom &&
        shippingPrice.validTo &&
        [
          shippingPrice,
          ...(shippingPrice.classifiedPriceBands ?? []),
          ...(shippingPrice.carrierPrices ?? []),
        ].every(({ priceBands }) => {
          const numberOfLastBands = priceBands?.filter((b) => b.maxCost === undefined)?.length;
          return numberOfLastBands === 1;
        }),
      ),
    [shippingPrice],
  );

  // Hide carriers that already have entries for every classification (null + all enum values)
  const carrierOptions = useMemo(() => {
    const allClassifications = [null, ...Object.values(ShipmentClassification)];
    const carrierSlots = (shippingPrice.carrierPrices ?? []).reduce<
      Map<string, Set<ShipmentClassification | null>>
    >((acc, { carrierName, classification }) => {
      const key = carrierName?.toLocaleLowerCase();
      if (!key) return acc;
      if (!acc.has(key)) acc.set(key, new Set());
      acc.get(key)!.add(classification ?? null);
      return acc;
    }, new Map());
    const fullyUsed = new Set(
      Array.from(carrierSlots.entries())
        .filter(([, used]) => allClassifications.every((c) => used.has(c)))
        .map(([key]) => key),
    );
    const alwaysExcluded = new Set(['cancelled', 'warehouse']);
    return (allCarriers?.items ?? []).filter(
      ({ name }) =>
        !alwaysExcluded.has(name.toLocaleLowerCase()) && !fullyUsed.has(name.toLocaleLowerCase()),
    );
  }, [allCarriers, shippingPrice]);

  useEffect(() => {
    setShippingPrice(
      shippingPriceData
        ? {
            ...shippingPriceData,
            priceBands: sortBands(shippingPriceData.priceBands),
            classifiedPriceBands: (shippingPriceData.classifiedPriceBands ?? []).map((cpb) => ({
              ...cpb,
              priceBands: sortBands(cpb.priceBands),
            })),
            carrierPrices: (shippingPriceData.carrierPrices ?? []).map((cp) => ({
              ...cp,
              priceBands: sortBands(cp.priceBands),
            })),
          }
        : {
            validFrom: new Date().toISOString(),
            priceBands: [],
            classifiedPriceBands: [],
          },
    );
    setHasChanges(false);
  }, [shippingPriceData]);

  const refresh = async () => {
    await Promise.all([refetch(), refetchAccounts(), refetchCarriers(), refetchAccountDetails()]);
  };

  const updateDetails = (newData: Partial<ShippingPriceDTO>) => {
    setShippingPrice({ ...shippingPrice, ...newData });
    setHasChanges(true);
  };

  const addNewGroup = (carrier: CarrierDTO, classification: ShipmentClassification | null) => {
    const newGroup: ShippingPriceCarrierPriceDTO = {
      priceBands: [],
      carrierId: carrier.id,
      carrierName: carrier.name,
      classification,
    };
    setShippingPrice({
      ...shippingPrice,
      carrierPrices: [...(shippingPrice.carrierPrices ?? []), newGroup],
    });
    setHasChanges(true);
  };

  const removeGroup = (groupIndex: number) => {
    if (!shippingPrice.carrierPrices) {
      return;
    }
    const group = shippingPrice.carrierPrices[groupIndex];
    if (!group) {
      return;
    }
    setShippingPrice({
      ...shippingPrice,
      carrierPrices: [
        ...shippingPrice.carrierPrices.slice(0, groupIndex),
        ...shippingPrice.carrierPrices.slice(groupIndex + 1),
      ],
    });
    setHasChanges(true);
  };

  const addBand = (band: ShippingPriceBandDTO) => {
    setShippingPrice({
      ...shippingPrice,
      priceBands: sortBands([...(shippingPrice.priceBands ?? []), band]),
    });
    setHasChanges(true);
  };

  const addGroupBand = (groupIndex: number, band: ShippingPriceBandDTO) => {
    if (!shippingPrice.carrierPrices) {
      return;
    }
    setShippingPrice({
      ...shippingPrice,
      carrierPrices: [
        ...shippingPrice.carrierPrices.slice(0, groupIndex),
        {
          ...shippingPrice.carrierPrices[groupIndex],
          priceBands: sortBands([
            ...(shippingPrice.carrierPrices[groupIndex].priceBands ?? []),
            band,
          ]),
        },
        ...shippingPrice.carrierPrices.slice(groupIndex + 1),
      ],
    });
    setHasChanges(true);
  };

  const updateBand = (index: number, band: Partial<ShippingPriceBandDTO>) => {
    setShippingPrice({
      ...shippingPrice,
      priceBands: sortBands(
        (shippingPrice.priceBands ?? []).map((b, i) => (i === index ? { ...b, ...band } : b)),
      ),
    });
    setHasChanges(true);
  };

  const updateGroupBand = (
    groupIndex: number,
    bandIndex: number,
    band: Partial<ShippingPriceBandDTO>,
  ) => {
    if (!shippingPrice.carrierPrices || !shippingPrice.carrierPrices[groupIndex]) {
      return;
    }
    setShippingPrice({
      ...shippingPrice,
      carrierPrices: [
        ...shippingPrice.carrierPrices.slice(0, groupIndex),
        {
          ...shippingPrice.carrierPrices[groupIndex],
          priceBands: sortBands(
            (shippingPrice.carrierPrices[groupIndex].priceBands ?? []).map((b, i) =>
              i === bandIndex ? { ...b, ...band } : b,
            ),
          ),
        },
        ...shippingPrice.carrierPrices.slice(groupIndex + 1),
      ],
    });
    setHasChanges(true);
  };

  const removeBand = (index: number) => {
    setShippingPrice({
      ...shippingPrice,
      priceBands: (shippingPrice.priceBands ?? []).filter((_, i) => i !== index),
    });
    setHasChanges(true);
  };

  const removeGroupBand = (groupIndex: number, bandIndex: number) => {
    if (!shippingPrice.carrierPrices || !shippingPrice.carrierPrices[groupIndex]) {
      return;
    }
    setShippingPrice({
      ...shippingPrice,
      carrierPrices: [
        ...shippingPrice.carrierPrices.slice(0, groupIndex),
        {
          ...shippingPrice.carrierPrices[groupIndex],
          priceBands: (shippingPrice.carrierPrices[groupIndex].priceBands ?? []).filter(
            (_, i) => i !== bandIndex,
          ),
        },
        ...shippingPrice.carrierPrices.slice(groupIndex + 1),
      ],
    });
    setHasChanges(true);
  };

  const addClassifiedGroup = (classification: ShipmentClassification) => {
    setShippingPrice({
      ...shippingPrice,
      classifiedPriceBands: [
        ...(shippingPrice.classifiedPriceBands ?? []),
        { classification, priceBands: [] },
      ],
    });
    setHasChanges(true);
  };

  const removeClassifiedGroup = (classifiedIndex: number) => {
    if (!shippingPrice.classifiedPriceBands) return;
    setShippingPrice({
      ...shippingPrice,
      classifiedPriceBands: [
        ...shippingPrice.classifiedPriceBands.slice(0, classifiedIndex),
        ...shippingPrice.classifiedPriceBands.slice(classifiedIndex + 1),
      ],
    });
    setHasChanges(true);
  };

  const addClassifiedBand = (classifiedIndex: number, band: ShippingPriceBandDTO) => {
    if (!shippingPrice.classifiedPriceBands?.[classifiedIndex]) return;
    setShippingPrice({
      ...shippingPrice,
      classifiedPriceBands: [
        ...shippingPrice.classifiedPriceBands.slice(0, classifiedIndex),
        {
          ...shippingPrice.classifiedPriceBands[classifiedIndex],
          priceBands: sortBands([
            ...(shippingPrice.classifiedPriceBands[classifiedIndex].priceBands ?? []),
            band,
          ]),
        },
        ...shippingPrice.classifiedPriceBands.slice(classifiedIndex + 1),
      ],
    });
    setHasChanges(true);
  };

  const updateClassifiedBand = (
    classifiedIndex: number,
    bandIndex: number,
    band: Partial<ShippingPriceBandDTO>,
  ) => {
    if (!shippingPrice.classifiedPriceBands?.[classifiedIndex]) return;
    setShippingPrice({
      ...shippingPrice,
      classifiedPriceBands: [
        ...shippingPrice.classifiedPriceBands.slice(0, classifiedIndex),
        {
          ...shippingPrice.classifiedPriceBands[classifiedIndex],
          priceBands: sortBands(
            (shippingPrice.classifiedPriceBands[classifiedIndex].priceBands ?? []).map((b, i) =>
              i === bandIndex ? { ...b, ...band } : b,
            ),
          ),
        },
        ...shippingPrice.classifiedPriceBands.slice(classifiedIndex + 1),
      ],
    });
    setHasChanges(true);
  };

  const removeClassifiedBand = (classifiedIndex: number, bandIndex: number) => {
    if (!shippingPrice.classifiedPriceBands?.[classifiedIndex]) return;
    setShippingPrice({
      ...shippingPrice,
      classifiedPriceBands: [
        ...shippingPrice.classifiedPriceBands.slice(0, classifiedIndex),
        {
          ...shippingPrice.classifiedPriceBands[classifiedIndex],
          priceBands: (shippingPrice.classifiedPriceBands[classifiedIndex].priceBands ?? []).filter(
            (_, i) => i !== bandIndex,
          ),
        },
        ...shippingPrice.classifiedPriceBands.slice(classifiedIndex + 1),
      ],
    });
    setHasChanges(true);
  };

  const { mutate: save, isPending: isSaving } = useGatewayMutation({
    mutationFn: async () => {
      const dto: Partial<ShippingPriceDTO> = {
        name: shippingPrice.name,
        accountId: shippingPrice.accountId,
        validFrom: shippingPrice.validFrom,
        validTo: shippingPrice.validTo,
        locationId: shippingPrice.locationId,
        priceBands: shippingPrice.priceBands,
        carrierPrices: shippingPrice.carrierPrices,
        classifiedPriceBands: shippingPrice.classifiedPriceBands,
      };
      if (isNew) {
        return shippingGateway.createShippingPrice(dto);
      }
      if (hasChanges) {
        delete dto.accountId;
        return shippingGateway.updateShippingPrice(id!, dto);
      }
      return shippingPrice;
    },
    onSuccess: (dto) => {
      setHasChanges(false);
      if (isNew) {
        void navigate(`/shipping/prices/${dto.id}`);
      }
    },
    linkedQuery: ['shippingPrices', id],
    successMessage: `Shipping price ${isNew ? 'created' : 'saved'}`,
  });

  return {
    models: {
      isNew,
      isSaving,
      isLoading,
      isLoadingAccounts,
      isLoadingCarriers,
      isDetailsModalOpen,
      isCarrierSelectionModalOpen,
      hasChanges,
      canSave,
      shippingPrice,
      accounts: allAccounts ?? [],
      account,
      location,
      carrierOptions,
      breadcrumbs,
      existingName,
      availableClassifications,
      existingCarrierClassifications,
    },
    operations: {
      refresh,
      updateDetails,
      save,
      addNewGroup,
      removeGroup,
      addBand,
      addGroupBand,
      updateBand,
      updateGroupBand,
      removeBand,
      removeGroupBand,
      addClassifiedGroup,
      removeClassifiedGroup,
      addClassifiedBand,
      updateClassifiedBand,
      removeClassifiedBand,
      openDetailsModal: () => setIsDetailsModalOpen(true),
      closeDetailsModal: () => setIsDetailsModalOpen(false),
      openCarrierSelectionModal: () => setIsCarrierSelectionModalOpen(true),
      closeCarrierSelectionModal: () => setIsCarrierSelectionModalOpen(false),
    },
  };
}
